import React, { useState, useEffect, useCallback } from 'react';
import { stageAPI, documentsAPI } from '../services/api';
import { CasesLivrables, ActionLigne } from './StageCertifCommun';
// Lot C.4a : « Voir le message envoyé » — le message RÉEL, tel qu'il est parti.
import { StageMessageEnvoye } from './StageFenetreEnvoi';
// Lot C.4b : récapitulatif AVANT, confirmation APRÈS (jamais alert/confirm).
import StageModale from './StageModale';
// Lot D.1 : la conservation des documents (bloc du panneau, mention « purgé »).
import { BlocConservation, MentionPurge } from './DocumentsConservation';

/**
 * SUIVI DE STAGE — CERTIFICATION, le PANNEAU d'un dossier (lot C.1)
 * ==================================================================
 * Maquette : #certifOverlay. Identité ; certificateur et sa SOURCE (copié au lancement, C9) ;
 * chaque document (version, « Voir », décision du maître : cases, motif, signataire, date — ou
 * la déclaration hors entreprise et son CONTRÔLE ALÉATOIRE, C27) ; fichiers identiques (CB6,
 * l'autre étudiant NOMMÉ) ; liens ; historique ; « Recommencer le dossier » et « Saisir le contact » (lot C.4b).
 *
 * ⚠️ Une ligne d'APERÇU (processus jamais lancé, CB1) n'a pas de dossier : le panneau montre
 * ce que la ligne sait — le certificateur qui serait copié, les livrables attendus — sans
 * rien demander au serveur.
 */

const REGIMES = { initiale: 'Formation initiale', apprentis: 'Apprentissage', fc: 'Formation continue' };

const momentFr = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const o = { timeZone: 'Europe/Paris' };
    return d.toLocaleDateString('fr-FR', o) + ' à ' + d.toLocaleTimeString('fr-FR', { ...o, hour: '2-digit', minute: '2-digit' });
};

function Champ({ label, children, large }) {
    return (
        <div className="detail-field-inline" style={large ? { gridColumn: '1 / -1' } : undefined}>
            <label>{label}</label><span>{children ?? '—'}</span>
        </div>
    );
}

function Titre({ icone, children }) {
    return <div className="detail-section-title"><span><i className={'fa-solid ' + icone} /> {children}</span></div>;
}

const BADGES = {
    certifie: ['badge-ok', 'Certifié'], a_corriger: ['badge-urgent', 'Ne peut pas être certifié'],
    corrige: ['badge-sent', 'Nouvelle version déposée'], transmis: ['badge-wait', 'En attente du maître'],
    brouillon: ['badge-wait', 'Déposé, non validé'], a_deposer: ['badge-wait', 'Attendu'], declare: ['badge-wait', 'Hors entreprise (déclaré)']
};

/** Le contrôle aléatoire d'une déclaration (C27, CB5 : append-only — un nouveau contrôle s'ajoute). */
function BlocControle({ declaration, modifiable, onEnregistrer }) {
    const [resultat, setResultat] = useState('conforme');
    const [note, setNote] = useState('');
    const [erreur, setErreur] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const dernier = declaration.controles[0];
    const enregistrer = async () => {
        if (resultat === 'non_conforme' && !note.trim()) { setErreur('Une note est obligatoire quand la déclaration est non conforme.'); return; }
        setEnCours(true); setErreur(null);
        const e = await onEnregistrer(declaration.id, resultat, note.trim());
        setEnCours(false);
        if (e) setErreur(e); else { setNote(''); setResultat('conforme'); }
    };
    return (
        <div className="certif-controle">
            <div className="certif-controle-ligne">
                <span style={{ fontSize: 11, fontWeight: 600 }}><i className="fa-solid fa-magnifying-glass" /> Contrôle aléatoire</span>
                {dernier ? (
                    <span className={'certif-controle-etat ' + (dernier.resultat === 'conforme' ? 'ok' : 'non')}>
                        {dernier.resultat === 'conforme' ? 'conforme' : 'non conforme'} — {dernier.par}, le {momentFr(dernier.le)}
                        {dernier.note ? ` : « ${dernier.note} »` : ''}
                        {declaration.controles.length > 1 ? ` (+ ${declaration.controles.length - 1} contrôle${declaration.controles.length > 2 ? 's' : ''} précédent${declaration.controles.length > 2 ? 's' : ''}, voir l'historique)` : ''}
                    </span>
                ) : <span style={{ fontSize: 11, color: '#64748b' }}>non contrôlé</span>}
            </div>
            {modifiable && (
                <div className="certif-controle-ligne">
                    <select className="input-sm" style={{ width: 'auto' }} value={resultat} onChange={(e) => setResultat(e.target.value)} aria-label="Résultat du contrôle">
                        <option value="conforme">Conforme</option>
                        <option value="non_conforme">Non conforme</option>
                    </select>
                    <input className="input-sm" style={{ flex: 1, minWidth: 160 }} value={note} maxLength={1000}
                           onChange={(e) => setNote(e.target.value)} placeholder="Note (obligatoire si non conforme)" aria-label="Note du contrôle" />
                    <button type="button" className="btn btn-outline btn-sm" onClick={enregistrer} disabled={enCours}>
                        <i className={'fa-solid ' + (enCours ? 'fa-circle-notch stg-tourne' : 'fa-check')} /> {dernier ? 'Enregistrer un nouveau contrôle' : 'Enregistrer le contrôle'}
                    </button>
                </div>
            )}
            {erreur && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
        </div>
    );
}

function CarteDocument({ doc, departementId, modifiable, onEnregistrer, onErreur }) {
    const [badge, libelleBadge] = BADGES[doc.etat] || ['badge-wait', doc.etat];
    const c = doc.courante;
    const voir = async () => {
        // ⚠️ L'onglet s'ouvre AVANT la lecture (sinon le navigateur le bloque) — même geste que le panneau du Stage.
        const fenetre = window.open('', '_blank');
        try {
            const r = await documentsAPI.lire(c.document_id, departementId);
            const url = URL.createObjectURL(r.data);
            if (fenetre) fenetre.location.href = url; else onErreur('Le navigateur a bloqué le nouvel onglet : autorisez les fenêtres pour ce site.');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { if (fenetre) fenetre.close(); onErreur("Le document n'a pas pu être ouvert."); }
    };
    const classe = doc.etat === 'a_corriger' ? ' non' : doc.etat === 'declare' ? ' hors' : '';
    return (
        <div className={'certif-carte-doc' + classe}>
            <div className="certif-carte-titre">
                <b>{doc.numero}. {doc.libelle}</b>
                <span className={'badge ' + badge}>{libelleBadge}</span>
                {c && (c.purge_le
                    ? <span style={{ marginLeft: 'auto' }}><MentionPurge purgeLe={c.purge_le} /></span>
                    : <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={voir}><i className="fa-solid fa-eye" /> Voir</button>)}
            </div>
            {c && (
                <div className="certif-carte-info">
                    {c.nom_fichier} · version {c.version} · déposé le {momentFr(c.depose_le)}
                    {c.purge_le && c.empreinte ? <> · empreinte <code>{c.empreinte.slice(0, 4)}…{c.empreinte.slice(-4)}</code> · contenu effacé, la preuve reste</> : ''}
                    {doc.decision ? ` · ${doc.decision.decision === 'certifie' ? 'certifié' : 'décision'} le ${momentFr(doc.decision.decide_le)} par ${doc.decision.signataire}` : ''}
                    {c.lu_par_maitre_le ? ` · ouvert par le maître le ${momentFr(c.lu_par_maitre_le)}` : ''}
                </div>
            )}
            {doc.decision?.decision === 'certifie' && (
                <div className="certif-carte-cases">
                    {doc.decision.cases.map((x) => (
                        <span key={x.cle}><i className={'fa-solid ' + (x.coche ? 'fa-check' : 'fa-xmark')} /> {x.libelle}</span>
                    ))}
                </div>
            )}
            {doc.motif && <div className="certif-carte-motif"><b>Motif :</b> {doc.motif}</div>}
            {doc.etat === 'a_corriger' && <div className="certif-carte-info"><i className="fa-solid fa-arrow-turn-down" style={{ transform: 'rotate(90deg)' }} /> Renvoyé à l'étudiant — en attente de la nouvelle version</div>}
            {doc.declaration && (
                <>
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                        L'étudiant déclare avoir traité le sujet proposé par la formation : <i>« {doc.declaration.precision} »</i>
                    </div>
                    <div className="certif-carte-info">
                        <i className="fa-solid fa-lock" /> Déclaré le {momentFr(doc.declaration.declare_le)}, sous l'avertissement : « {doc.declaration.avertissement} » — pas de dépôt, pas de certification
                    </div>
                    <BlocControle declaration={doc.declaration} modifiable={modifiable} onEnregistrer={onEnregistrer} />
                </>
            )}
            {(c?.identiques || []).map((t, i) => (
                <div key={i} className="certif-carte-identique"><i className="fa-solid fa-triangle-exclamation" /> {t}</div>
            ))}
        </div>
    );
}

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

/**
 * C21 — le récapitulatif AVANT « Recommencer », avec les VRAIS chiffres du dossier (maquette #certifRecommencer).
 * Le motif est obligatoire ; le serveur le redemande de toute façon.
 */
function ModaleRecommencer({ nom, r, enCours, erreur, onValider, onFermer }) {
    const [motif, setMotif] = useState('');
    const [manque, setManque] = useState(false);
    const n = r.documents + r.declarations;
    const archives = [r.documents ? pluriel(r.documents, 'document déposé', 'documents déposés') : null,
                      r.declarations ? pluriel(r.declarations, 'déclaration hors entreprise', 'déclarations hors entreprise') : null].filter(Boolean);
    return (
        <StageModale titre={`Recommencer le dossier de ${nom}`} icone="fa-rotate-left" ton="danger" enCours={enCours}
                     libelleValider="Recommencer" onFermer={onFermer}
                     onValider={() => { if (!motif.trim()) { setManque(true); return; } onValider(motif.trim()); }}>
            <p style={{ marginBottom: 8 }}>Le dossier repart à « Lien à envoyer » :</p>
            <ul style={{ margin: '0 0 10px 18px', lineHeight: 1.7 }}>
                <li>{r.lien_maitre
                    ? <>le lien de l'étudiant <b>et celui du maître</b> ne s'ouvrent plus ;</>
                    : <>le lien de l'étudiant ne s'ouvre plus ;</>}</li>
                {archives.length > 0 && <li>{archives.join(' et ')} {n > 1 ? 'sont' : 'est'} <b>archivé{n > 1 ? 's' : ''}</b>, pas supprimé{n > 1 ? 's' : ''} ;</li>}
                {r.certifications > 0 && <li>{r.certifications > 1 ? 'les décisions déjà données par le maître sont' : 'la décision déjà donnée par le maître est'} <b>annulée{r.certifications > 1 ? 's' : ''}</b> — l'historique en garde la trace ;</li>}
                <li>le certificateur reste celui du dossier ; un nouveau lien sera à envoyer à l'étudiant.</li>
            </ul>
            <label htmlFor="certif-motif-recommencer" style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
                Motif <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea id="certif-motif-recommencer" className="input-sm" style={{ width: '100%', minHeight: 60 }} maxLength={1000}
                      value={motif} onChange={(e) => { setMotif(e.target.value); setManque(false); }}
                      placeholder="Ex : l'étudiant a déposé le rapport d'une autre SAÉ" />
            {manque && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}><i className="fa-solid fa-circle-exclamation" /> Le motif est obligatoire.</div>}
            {erreur && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>
                <i className="fa-solid fa-lock" /> Sur un dossier <b>terminé</b>, cette action est réservée à un administrateur.
            </div>
        </StageModale>
    );
}

/** CR8 — le contact saisi par le secrétariat, copié dans CE dossier seulement. */
function ModaleSaisirContact({ nom, alternant, enCours, erreur, onValider, onFermer }) {
    const [f, setF] = useState({ nom: '', fonction: '', email: '', entreprise: '' });
    const [manque, setManque] = useState(null);
    const champ = (cle, libelle, obligatoire, type = 'text') => (
        <div className="detail-field-inline">
            <label htmlFor={'certif-saisie-' + cle}>{libelle}{obligatoire && <span style={{ color: '#dc2626' }}> *</span>}</label>
            <input id={'certif-saisie-' + cle} type={type} className="input-sm" value={f[cle]} maxLength={cle === 'entreprise' || cle === 'email' ? 255 : 200}
                   onChange={(e) => { setF({ ...f, [cle]: e.target.value }); setManque(null); }} />
        </div>
    );
    const valider = () => {
        if (!f.nom.trim()) { setManque('Le nom est obligatoire.'); return; }
        if (!f.email.trim()) { setManque('Le courriel est obligatoire : sans lui, aucun lien ne peut partir.'); return; }
        onValider({ nom: f.nom.trim(), fonction: f.fonction.trim(), email: f.email.trim(), entreprise: f.entreprise.trim() });
    };
    return (
        <StageModale titre={`Saisir le contact du certificateur — ${nom}`} icone="fa-user-pen" enCours={enCours}
                     libelleValider="Enregistrer le contact" onValider={valider} onFermer={onFermer}>
            <p style={{ marginBottom: 10, fontSize: 12 }}>
                Le contact est copié dans <b>ce dossier seulement</b> (source « Saisi par le secrétariat »). La fiche de liaison
                {alternant ? ' et le registre Alternance ne changent' : ' ne change'} pas. Aucun message ne part à l'enregistrement.
            </p>
            <div className="detail-fields-2col">
                {champ('nom', 'Nom et prénom', true)}
                {champ('fonction', 'Fonction', false)}
                {champ('email', 'Courriel', true, 'email')}
                {champ('entreprise', 'Entreprise', false)}
            </div>
            {(manque || erreur) && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 6 }}><i className="fa-solid fa-circle-exclamation" /> {manque || erreur}</div>}
        </StageModale>
    );
}

function StageCertifPanneau({ ligne, processus, departementId, modifiable, onFermer, onAction, onChange }) {
    const [dossier, setDossier] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [messageVu, setMessageVu] = useState(null);      // l'id du journal ouvert dans « Voir le message envoyé »
    // Lot C.4b — la modale ouverte : 'recommencer' | 'saisir' | { succes: 'recommence' | 'saisi' }.
    const [modale, setModale] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [erreurModale, setErreurModale] = useState(null);

    const lire = useCallback(async () => {
        if (!ligne.dossier_id) return;
        try {
            const r = await stageAPI.getCertifDossier(ligne.dossier_id, departementId);
            setDossier(r.data);
        } catch (e) { setErreur(e.response?.data?.error || 'Lecture du dossier impossible'); }
    }, [ligne.dossier_id, departementId]);
    useEffect(() => { lire(); }, [lire]);

    // Échap referme — la modale ouverte d'abord (lot C.4b), le panneau ensuite ; rien pendant une écriture.
    useEffect(() => {
        const auClavier = (e) => {
            if (e.key !== 'Escape') return;
            if (modale) { if (!enCours) { setModale(null); setErreurModale(null); } return; }
            onFermer();
        };
        window.addEventListener('keydown', auClavier);
        return () => window.removeEventListener('keydown', auClavier);
    }, [onFermer, modale, enCours]);

    /** Rend `null` si c'est enregistré, sinon le message du refus. */
    const enregistrerControle = async (declarationId, resultat, note) => {
        try {
            const r = await stageAPI.controlerDeclarationCertif(ligne.dossier_id, departementId, { declarationId, resultat, note });
            setDossier(r.data);
            onChange();
            return null;
        } catch (e) { return e.response?.data?.error || "Le contrôle n'a pas pu être enregistré."; }
    };

    /** Lot C.4b — un geste d'écriture sur le dossier : la réponse EST le panneau relu. */
    const ecrire = async (appel, succes) => {
        setEnCours(true); setErreurModale(null);
        try {
            const r = await appel();
            setDossier(r.data);
            setModale({ succes });
            onChange();
        } catch (e) {
            setErreurModale(e.response?.data?.error || "L'enregistrement a échoué.");
        } finally { setEnCours(false); }
    };
    const fermerModale = () => { if (!enCours) { setModale(null); setErreurModale(null); } };

    // CF6 : un dossier au maître INCONNU montre ce que les fiches savent aujourd'hui (la raison, ou le contact trouvé).
    const copie = dossier && !dossier.maitre_inconnu;
    const c = copie ? dossier.certificateur : (ligne.certificateur || {});
    const statut = dossier?.statut || ligne.statut;
    const alternant = ['apprentis', 'fc'].includes(ligne.regime);
    const maitreInconnu = dossier ? dossier.maitre_inconnu : ligne.maitre_inconnu;
    const nomEtudiant = `${ligne.etudiant.nom} ${ligne.etudiant.prenom}`;
    const rec = dossier?.recommencer;
    const recInterdit = !!(rec && rec.reserve_admin && !dossier.est_admin);
    return (
        <div className="stg-panneau-fond" onClick={onFermer}>
            <div className="stg-panneau certif-panneau" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Dossier de certification">
                <div className="stg-panneau-corps">
                    <div className="stg-panneau-entete">
                        <i className="fa-solid fa-stamp" style={{ color: 'var(--stg-vert-d)', marginTop: 3 }} />
                        <div style={{ flex: 1 }}>
                            <h3>{ligne.etudiant.nom} {ligne.etudiant.prenom}</h3>
                            <div className="stg-panneau-sous">
                                N° {ligne.etudiant.code_etudiant || '—'} · {processus.formation_nom} — {REGIMES[ligne.regime] || ligne.regime} · Processus « {processus.nom} »
                            </div>
                        </div>
                        <ActionLigne action={dossier?.action ?? ligne.action} statut={statut} modifiable={modifiable} onAction={onAction} />
                        <button type="button" className="action-btn" onClick={onFermer} title="Fermer (Échap)"><i className="fa-solid fa-xmark" /></button>
                    </div>

                    <div className="certif-panneau-bandeau">
                        <div className="certif-panneau-bandeau-titre">Livrables — {ligne.resume}</div>
                        <CasesLivrables cases={ligne.cases} />
                    </div>

                    {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
                    {/* Lot C.4a : le dernier message n'est pas parti — dit en tête, comme sur la ligne. */}
                    {dossier?.alerte && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-triangle-exclamation" /><span>{dossier.alerte}</span></div>}

                    {ligne.apercu && (
                        <div className="stg-encart" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 14 }}>
                            <i className="fa-solid fa-circle-info" />
                            <span>Le dossier n'existe pas encore : il naîtra à l'envoi du lien, et le certificateur ci-dessous y sera <b>copié</b> ce jour-là.</span>
                        </div>
                    )}

                    <div className="detail-section">
                        <Titre icone="fa-user-tie">Certificateur</Titre>
                        {c.manque && !c.nom ? (
                            <div style={{ fontSize: 12, color: '#b91c1c' }}>
                                <i className="fa-solid fa-triangle-exclamation" /> Aucun contact connu — {c.manque}.
                                <div style={{ color: '#64748b', marginTop: 4 }}>
                                    {['apprentis', 'fc'].includes(ligne.regime)
                                        ? 'Complétez le maître d\'apprentissage dans le registre Alternance.'
                                        : 'La fiche de liaison doit être validée par le maître de stage (Stagiaires → Tableau de bord).'}
                                </div>
                            </div>
                        ) : (
                            <div className="detail-fields-2col">
                                <Champ label={['apprentis', 'fc'].includes(ligne.regime) ? "Maître d'apprentissage" : 'Maître de stage'}>
                                    {c.nom}{c.fonction ? ` — ${c.fonction}` : ''}
                                </Champ>
                                <Champ label="Entreprise">{c.entreprise}</Champ>
                                <Champ label="Email">{c.email || <span style={{ color: '#b91c1c' }}>{c.manque}</span>}</Champ>
                                <Champ label="Source">
                                    {copie && c.source === 'saisi'
                                        // Lot C.4b (CR8) : tapé pour CE dossier seulement — ni fiche, ni registre derrière.
                                        ? <>{c.source_libelle} <span style={{ color: '#64748b', fontWeight: 400 }}>(pour ce dossier seulement — la fiche de liaison et le registre Alternance n'ont pas changé)</span></>
                                        : copie
                                        ? <>{c.source_libelle || '—'} <span style={{ color: '#64748b', fontWeight: 400 }}>(copié le {momentFr(c.copie_le)} — une correction de la fiche ne le change pas)</span></>
                                        : <>{c.source === 'maitre_apprentissage' ? 'Registre Alternance' : 'Fiche de liaison (validée par le maître)'} <span style={{ color: '#64748b', fontWeight: 400 }}>(lu aujourd'hui{dossier ? ' — copié dans le dossier au prochain envoi au maître' : ''})</span></>}
                                </Champ>
                            </div>
                        )}
                        {/* CR9 — corriger à la source, ou saisir pour CE dossier seulement (CR10 : le dossier doit exister). */}
                        {maitreInconnu && modifiable && (
                            <div className="certif-contact-actions">
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => onAction('completer')}
                                        title={alternant ? 'Ouvrir le registre Alternance, la recherche pré-remplie' : 'Ouvrir le tableau des stagiaires, la recherche pré-remplie'}>
                                    <i className="fa-solid fa-user-plus" /> Compléter le contact
                                </button>
                                {dossier?.saisir_contact ? (
                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { setErreurModale(null); setModale('saisir'); }}
                                            title="Taper le contact du certificateur pour ce dossier seulement">
                                        <i className="fa-solid fa-user-pen" /> Saisir le contact
                                    </button>
                                ) : ligne.apercu && (
                                    <span style={{ fontSize: 11, color: '#64748b' }}>Saisir le contact directement sera possible dès que le dossier existera (au premier envoi du lien).</span>
                                )}
                            </div>
                        )}
                    </div>

                    {ligne.dossier_id && !dossier && !erreur && (
                        <div className="stg-panneau-attente"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture du dossier…</div>
                    )}

                    {dossier && (
                        <>
                            <div className="detail-section">
                                <Titre icone="fa-file-lines">Documents</Titre>
                                {dossier.documents.map((d) => (
                                    <CarteDocument key={d.livrable_id} doc={d} departementId={departementId} modifiable={modifiable}
                                                   onEnregistrer={enregistrerControle} onErreur={setErreur} />
                                ))}
                            </div>

                            <div className="detail-section">
                                <Titre icone="fa-paper-plane">Envois</Titre>
                                <div className="detail-fields-2col">
                                    <Champ label="Lien étudiant">
                                        {dossier.liens.etudiant
                                            ? <>envoyé le {momentFr(dossier.liens.etudiant.envoye_le)}{dossier.liens.etudiant.ouvert_le ? ` · ouvert le ${momentFr(dossier.liens.etudiant.ouvert_le)}` : ' · jamais ouvert'}</>
                                            : 'pas encore envoyé'}
                                    </Champ>
                                    <Champ label="Lien du maître">
                                        {dossier.liens.maitre
                                            ? <>envoyé le {momentFr(dossier.liens.maitre.envoye_le)}{dossier.liens.maitre.ouvert_le ? ` · ouvert le ${momentFr(dossier.liens.maitre.ouvert_le)}` : ' · jamais ouvert'}</>
                                            : dossier.liens.depot_valide_le ? `dépôt validé le ${momentFr(dossier.liens.depot_valide_le)}` : 'pas encore envoyé'}
                                    </Champ>
                                </div>
                                {/* Lot C.4a — le journal du dossier : chaque message tel qu'il est parti. */}
                                <div className="certif-envois">
                                    {(dossier.envois || []).length === 0 && <div style={{ fontSize: 11, color: '#64748b' }}>Aucun message encore.</div>}
                                    {(dossier.envois || []).map((x) => (
                                        <div key={x.id} className={'certif-envoi' + (x.ok ? '' : ' echec')}>
                                            <span className="certif-envoi-date">{momentFr(x.le)}</span>
                                            <span>
                                                <b>{x.quoi}</b> → {x.a} <span style={{ color: '#64748b' }}>· {x.par}</span>
                                                {!x.ok && <div className="certif-envoi-erreur"><i className="fa-solid fa-triangle-exclamation" /> {x.erreur || 'échec'}</div>}
                                            </span>
                                            <button type="button" className="lien-voir" onClick={() => setMessageVu(x.id)}>Voir le message envoyé</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="detail-section">
                                <Titre icone="fa-clock-rotate-left">Historique</Titre>
                                {dossier.historique.length === 0 && <div style={{ fontSize: 11, color: '#64748b' }}>Rien encore.</div>}
                                {dossier.historique.map((h, i) => (
                                    <div key={i} className="certif-hist">
                                        <span style={{ color: '#64748b' }}>{momentFr(h.date)}</span>
                                        <span>{h.auteur ? <b>{h.auteur}</b> : '—'} · {h.texte}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Lot D.1 — la conservation des documents du dossier (avant « Recommencer », comme la maquette). */}
                            <BlocConservation departementId={departementId} objetTable="certif_dossiers" objetId={ligne.dossier_id} onChange={lire} />

                            {/* Recommencer : dans le panneau, jamais sur la ligne (une seule action par ligne). CR2 : absent s'il n'y a rien à défaire. */}
                            {rec && (
                                <div className="detail-section certif-recommencer">
                                    <div style={{ flex: 1, fontSize: 11, color: '#64748b' }}>
                                        L'étudiant s'est trompé de fichiers ? Le dossier repart de zéro : liens coupés, dépôts archivés (jamais supprimés), certifications annulées.
                                    </div>
                                    <button type="button" className="btn btn-outline btn-sm" style={{ borderColor: '#fecaca', color: '#b91c1c' }}
                                            disabled={!modifiable || recInterdit}
                                            title={!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.'
                                                : recInterdit ? 'Ce dossier est terminé : le recommencer est réservé à un administrateur du département.'
                                                : "Le récapitulatif s'affiche d'abord : rien n'est fait sans votre confirmation."}
                                            onClick={() => { setErreurModale(null); setModale('recommencer'); }}>
                                        <i className="fa-solid fa-rotate-left" /> Recommencer le dossier
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {messageVu && <StageMessageEnvoye envoiId={messageVu} departementId={departementId} onFermer={() => setMessageVu(null)} />}

            {/* ⚠️ Les modales vivent DANS le fond du panneau : sans cet arrêt, un clic sur leur fond refermerait aussi le panneau. */}
            <div onClick={(e) => e.stopPropagation()}>
            {modale === 'recommencer' && rec && (
                <ModaleRecommencer nom={nomEtudiant} r={rec} enCours={enCours} erreur={erreurModale} onFermer={fermerModale}
                                   onValider={(motif) => ecrire(() => stageAPI.recommencerDossierCertif(ligne.dossier_id, departementId, motif), 'recommence')} />
            )}
            {modale === 'saisir' && (
                <ModaleSaisirContact nom={nomEtudiant} alternant={alternant} enCours={enCours} erreur={erreurModale} onFermer={fermerModale}
                                     onValider={(data) => ecrire(() => stageAPI.saisirCertificateurCertif(ligne.dossier_id, departementId, data), 'saisi')} />
            )}
            {/* CR4 — après « Recommencer » : rien ne part tout seul, le nouveau lien s'envoie par la fenêtre (message modifiable). */}
            {modale?.succes === 'recommence' && (
                <StageModale titre="Dossier recommencé" icone="fa-circle-check" ton="succes" onFermer={() => setModale(null)}
                             libelleAnnuler="Plus tard" libelleValider="Envoyer le nouveau lien maintenant"
                             onValider={() => { setModale(null); onAction('envoyer'); }}>
                    <p>Les liens sont coupés, les documents archivés, et l'historique garde le motif.</p>
                    <p style={{ marginTop: 6 }}>
                        Le dossier de {nomEtudiant} est à « {dossier?.statut === 'sans_certificateur' ? 'Sans certificateur' : 'Lien à envoyer'} » :
                        <b> aucun message n'est parti</b>. Le nouveau lien s'envoie par la fenêtre d'envoi, où vous relisez le message.
                    </p>
                </StageModale>
            )}
            {modale?.succes === 'saisi' && (
                <StageModale titre="Contact enregistré" icone="fa-circle-check" ton="succes" onFermer={() => setModale(null)}>
                    <p>{dossier?.certificateur?.nom} est copié dans le dossier de {nomEtudiant} (source « Saisi par le secrétariat »).</p>
                    <p style={{ marginTop: 6 }}>
                        Aucun message n'est parti.
                        {dossier?.action ? <> L'action de la ligne est maintenant <b>« {dossier.action.libelle} »</b>.</> : null}
                    </p>
                </StageModale>
            )}
            </div>
        </div>
    );
}

export default StageCertifPanneau;
